import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  Row, Col, Card, Input, Button, Typography, Space, Tag, Alert, Empty,
  List, Divider, Collapse, Statistic, Badge, Spin
} from 'antd';
import {
  SendOutlined,
  PlusOutlined,
  MessageOutlined,
  FileTextOutlined,
  ThunderboltOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  CloseCircleOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import { askQuestion, fetchSessions, createSession, setCurrentSession, fetchSessionMessages, deleteSession } from '../store/conversationsSlice';
import MarkdownAnswer from '../components/MarkdownAnswer';
import DocumentSelector from '../components/DocumentSelector';

const { TextArea } = Input;
const { Title, Paragraph, Text } = Typography;
const { Panel } = Collapse;

function AskQuestion() {
  const dispatch = useDispatch();
  const { currentAnswer, currentSessionId, sessions, sessionMessages, loading, messagesLoading } = useSelector(
    (state) => state.conversations
  );
  const [question, setQuestion] = useState('');
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [selectedFolders, setSelectedFolders] = useState([]);
  const answerRef = useRef(null);

  useEffect(() => {
    dispatch(fetchSessions());
  }, [dispatch]);

  useEffect(() => {
    if (currentAnswer && answerRef.current) {
      answerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [currentAnswer]);

  const handleSessionClick = (sessionId) => {
    dispatch(fetchSessionMessages(sessionId));
  };

  const handleDeleteSession = (e, sessionId) => {
    e.stopPropagation();
    dispatch(deleteSession(sessionId));
  };

  const handleSubmit = async () => {
    if (!question.trim()) return;

    await dispatch(askQuestion({ 
      question, 
      sessionId: currentSessionId,
      documentIds: selectedDocuments.length > 0 ? selectedDocuments : null,
      folderIds: selectedFolders.length > 0 ? selectedFolders : null
    }));
    setQuestion('');
  };

  const handleDocumentSelection = ({ documentIds, folderIds }) => {
    setSelectedDocuments(documentIds || []);
    setSelectedFolders(folderIds || []);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleNewSession = () => {
    dispatch(createSession('New Conversation'));
  };

  const getConfidenceColor = (score) => {
    if (score >= 70) return '#16A34A';
    if (score >= 40) return '#D97706';
    return '#DC2626';
  };

  const getConfidenceIcon = (score) => {
    if (score >= 70) return <CheckCircleOutlined />;
    if (score >= 40) return <ExclamationCircleOutlined />;
    return <CloseCircleOutlined />;
  };

  const getConfidenceLabel = (score) => {
    if (score >= 70) return 'High Confidence';
    if (score >= 40) return 'Medium Confidence';
    return 'Low Confidence';
  };

  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      <div style={{ marginBottom: '32px' }}>
        <Title level={2} style={{ marginBottom: '8px' }}>
          Ask Questions
        </Title>
        <Paragraph type="secondary" style={{ fontSize: '16px', marginBottom: 0 }}>
          Get AI-powered answers from your technical documentation with source citations
        </Paragraph>
      </div>

      <Row gutter={[24, 24]}>
        <Col xs={24} lg={16}>
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Card bordered={false} title={<Space><MessageOutlined />Ask a Question</Space>}>
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <TextArea
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="What safety procedures should be followed during kiln maintenance?"
                  autoSize={{ minRows: 4, maxRows: 8 }}
                  disabled={loading}
                  style={{ fontSize: '15px' }}
                />
                
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Text type="secondary" style={{ fontSize: '13px' }}>
                    Press Enter to send, Shift+Enter for new line
                  </Text>
                  <Space>
                    <Button 
                      onClick={handleNewSession}
                      icon={<PlusOutlined />}
                    >
                      New Session
                    </Button>
                    <Button 
                      type="primary"
                      onClick={handleSubmit}
                      disabled={loading || !question.trim()}
                      icon={loading ? <Spin size="small" /> : <SendOutlined />}
                      size="large"
                    >
                      {loading ? 'Searching...' : 'Get Answer'}
                    </Button>
                  </Space>
                </div>
              </Space>
            </Card>

            {messagesLoading && (
              <Card bordered={false}>
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <Spin size="large" />
                  <div style={{ marginTop: '16px' }}>
                    <Text type="secondary">Loading conversation history...</Text>
                  </div>
                </div>
              </Card>
            )}

            {!messagesLoading && sessionMessages.length > 0 && !currentAnswer && (
              <Card 
                bordered={false}
                title={
                  <Space>
                    <MessageOutlined style={{ color: '#0B3D91' }} />
                    <Text strong>Conversation History</Text>
                    <Badge count={sessionMessages.length} style={{ backgroundColor: '#0B3D91' }} />
                  </Space>
                }
              >
                <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
                  {sessionMessages.map((msg, index) => (
                    <div 
                      key={index} 
                      style={{ 
                        marginBottom: index < sessionMessages.length - 1 ? '24px' : 0,
                        paddingBottom: index < sessionMessages.length - 1 ? '24px' : 0,
                        borderBottom: index < sessionMessages.length - 1 ? '1px solid #F0F0F0' : 'none'
                      }}
                    >
                      <div style={{ marginBottom: '12px' }}>
                        <Space>
                          <Tag color="blue">Q</Tag>
                          <Text strong style={{ fontSize: '15px' }}>{msg.question}</Text>
                        </Space>
                      </div>
                      <div style={{ 
                        backgroundColor: '#FAFAFA', 
                        padding: '16px', 
                        borderRadius: '8px',
                        marginBottom: '12px'
                      }}>
                        <Space style={{ marginBottom: '8px' }}>
                          <Tag color="green">A</Tag>
                          <Tag 
                            icon={getConfidenceIcon(msg.confidence_score)}
                            color={
                              msg.confidence_score >= 70 ? 'success' :
                              msg.confidence_score >= 40 ? 'warning' : 'error'
                            }
                          >
                            {msg.confidence_score}% {getConfidenceLabel(msg.confidence_score)}
                          </Tag>
                        </Space>
                        <div style={{ fontSize: '14px' }}>
                          <MarkdownAnswer content={msg.answer} />
                        </div>
                      </div>
                      {msg.sources && msg.sources.length > 0 && (
                        <Collapse size="small">
                          <Panel 
                            header={<Text type="secondary">{msg.sources.length} source{msg.sources.length !== 1 ? 's' : ''}</Text>}
                            key="sources"
                          >
                            {msg.sources.map((source, srcIdx) => (
                              <div key={srcIdx} style={{ marginBottom: srcIdx < msg.sources.length - 1 ? '8px' : 0 }}>
                                <Space>
                                  <FileTextOutlined style={{ color: '#0B3D91' }} />
                                  <Text>{source.document}</Text>
                                  <Tag size="small">Page {source.page}</Tag>
                                </Space>
                              </div>
                            ))}
                          </Panel>
                        </Collapse>
                      )}
                      <div style={{ marginTop: '8px' }}>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          {new Date(msg.created_at).toLocaleString()}
                        </Text>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {currentAnswer && (
              <div ref={answerRef}>
                <Card 
                  bordered={false}
                  title={
                    <Space>
                      <MessageOutlined style={{ color: '#0B3D91' }} />
                      <Text strong>Answer</Text>
                    </Space>
                  }
                  extra={
                    <Tag 
                      icon={getConfidenceIcon(currentAnswer.confidence)}
                      color={
                        currentAnswer.confidence >= 70 ? 'success' :
                        currentAnswer.confidence >= 40 ? 'warning' : 'error'
                      }
                    >
                      {currentAnswer.confidence}% {getConfidenceLabel(currentAnswer.confidence)}
                    </Tag>
                  }
                >
                  <div style={{ fontSize: '16px', marginBottom: '24px' }}>
                    <MarkdownAnswer content={currentAnswer.answer} />
                  </div>

                  {currentAnswer.confidence < 70 && (
                    <Alert
                      message="Confidence Note"
                      description="The AI has medium to low confidence in this answer. Please verify information from the source documents before taking action."
                      type="warning"
                      showIcon
                      style={{ marginBottom: '24px' }}
                    />
                  )}

                  <Divider>Source Citations</Divider>

                  {currentAnswer.sources && currentAnswer.sources.length > 0 ? (
                    <Collapse accordion>
                      {currentAnswer.sources.map((source, idx) => (
                        <Panel 
                          header={
                            <Space>
                              <FileTextOutlined style={{ color: '#0B3D91' }} />
                              <Text strong>{source.document}</Text>
                              <Tag color="blue">Page {source.page}</Tag>
                            </Space>
                          }
                          key={idx}
                        >
                          <div style={{ backgroundColor: '#FAFAFA', padding: '16px', borderRadius: '6px' }}>
                            <Text style={{ fontSize: '14px', lineHeight: '1.6' }}>
                              "{source.excerpt}"
                            </Text>
                          </div>
                        </Panel>
                      ))}
                    </Collapse>
                  ) : (
                    <Empty 
                      description="No source citations available" 
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                  )}

                  <Divider />

                  <Row gutter={16}>
                    <Col span={12}>
                      <Statistic
                        title="Tokens Used"
                        value={currentAnswer.tokens_used?.toLocaleString() || 0}
                        prefix={<ThunderboltOutlined />}
                        valueStyle={{ fontSize: '20px' }}
                      />
                    </Col>
                    <Col span={12}>
                      <Statistic
                        title="Sources Found"
                        value={currentAnswer.sources?.length || 0}
                        prefix={<FileTextOutlined />}
                        valueStyle={{ fontSize: '20px' }}
                      />
                    </Col>
                  </Row>
                </Card>
              </div>
            )}

            {!currentAnswer && !loading && !messagesLoading && sessionMessages.length === 0 && (
              <Card bordered={false}>
                <Empty
                  image={<MessageOutlined style={{ fontSize: '64px', color: '#D1D5DB' }} />}
                  description={
                    <Space direction="vertical" size="small">
                      <Text type="secondary">No questions asked yet</Text>
                      <Text type="secondary" style={{ fontSize: '14px' }}>
                        Ask a question about your uploaded documents to get AI-powered answers
                      </Text>
                    </Space>
                  }
                />
              </Card>
            )}
          </Space>
        </Col>

        <Col xs={24} lg={8}>
          <DocumentSelector 
            selectedDocuments={selectedDocuments}
            selectedFolders={selectedFolders}
            onSelectionChange={handleDocumentSelection}
          />
          
          <Card 
            bordered={false}
            title={
              <Space>
                <MessageOutlined />
                <Text strong>Recent Sessions</Text>
                <Badge count={sessions.length} />
              </Space>
            }
            style={{ marginTop: '16px' }}
          >
            {sessions.length === 0 ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No conversation sessions yet"
              >
                <Button type="primary" onClick={handleNewSession} icon={<PlusOutlined />}>
                  Create First Session
                </Button>
              </Empty>
            ) : (
              <List
                dataSource={sessions.slice(0, 10)}
                renderItem={(session) => (
                  <List.Item
                    onClick={() => handleSessionClick(session.id)}
                    style={{
                      cursor: 'pointer',
                      backgroundColor: session.id === currentSessionId ? '#F0F7FF' : 'transparent',
                      borderRadius: '6px',
                      padding: '12px',
                      marginBottom: '8px',
                      border: session.id === currentSessionId ? '1px solid #0B3D91' : '1px solid transparent',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <List.Item.Meta
                      title={
                        <Text strong style={{ fontSize: '15px' }}>
                          {session.session_name || 'Untitled Session'}
                        </Text>
                      }
                      description={
                        <Space direction="vertical" size={0}>
                          <Text type="secondary" style={{ fontSize: '13px' }}>
                            {session.message_count} message{session.message_count !== 1 ? 's' : ''}
                          </Text>
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            {new Date(session.last_updated).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                          </Text>
                        </Space>
                      }
                      style={{ flex: 1 }}
                    />
                    <Button
                      type="text"
                      danger
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={(e) => {
                        e.stopPropagation();
                        const confirmed = window.confirm(`Delete this session? This cannot be undone.`);
                        if (confirmed) {
                          dispatch(deleteSession(session.id));
                        }
                      }}
                      style={{ marginLeft: '8px', flexShrink: 0 }}
                    />
                  </List.Item>
                )}
              />
            )}
          </Card>

          <Card 
            bordered={false}
            title="Tips for Better Answers"
            size="small"
            style={{ marginTop: '16px' }}
          >
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Text type="secondary" style={{ fontSize: '13px' }}>
                • Be specific with your questions
              </Text>
              <Text type="secondary" style={{ fontSize: '13px' }}>
                • Reference equipment names or procedures
              </Text>
              <Text type="secondary" style={{ fontSize: '13px' }}>
                • Check confidence scores before acting
              </Text>
              <Text type="secondary" style={{ fontSize: '13px' }}>
                • Review source citations for context
              </Text>
            </Space>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default AskQuestion;
